<template>
  <div
    ref="cb"
    tabindex="0"
    class="checkbox v-centerer inline"
    :class="{ 'checkbox--disabled': disabled, checked: value }"
    @click="clicked"
  >
    <div class="tick h-v-centerer" :class="{ round }">
      <Icon
        class="tick-img"
        :class="{ animated: !noAnimation }"
        :color="SColors[color]"
        :style="tickStyle"
      >
        {{ icon || checkboxCheckElement }}
      </Icon>
    </div>
    <span class="checkbox__title">{{ title }}</span>
  </div>
</template>

<script>
/**
 * Basic checkbox with nicer design
 *
 * @vue-prop {Boolean} [value=false] - True when the box is ticked
 * @vue-prop {String} [title=''] - String displayed to the right of the checkbox
 * @vue-prop {Boolean} [disabled=false] - Whether the box shouldn't be tickable
 * @vue-prop {String} [errMsg=''] - Error message to display when user tries to tick a disabled box
 * @vue-prop {String} [color='gray'] - Color String to use a different img file for the box
 * @vue-prop {String} [icon=null] - Custom icon if reuired (default is a check icon)
 */

import $ from 'jquery';
// Components
import Icon from '@/components/icon.vue';
// Utils
import { SColors } from '@/utils/constants';
import { checkboxCheckElement } from '@/assets/icons/';
import { calert, calertHide, getTextFgColor } from '@/utils/main';

export default {

  name: 'Checkbox',

  components: {
    Icon,
  },

  props: {
    value: Boolean,
    title: {
      type: String,
      default: '',
    },
    disabled: Boolean,
    errMsg: {
      type: String,
      default: '',
    },
    color: {
      type: String,
      default: 'blue',
      validator: v => v === null || !!SColors[v],
    },
    icon: {
      type: String,
      default: null,
    },
    stopPropagation: Boolean,
    round: Boolean,
    noAnimation: Boolean,
  },

  data() {
    return {
      SColors,
      checkboxCheckElement,
    };
  },

  computed: {
    /** Object: Style of the tick image */
    tickStyle() {
      return {
        background: this.value ? getTextFgColor(SColors[this.color]) : undefined,
      };
    },
  },

  mounted() {
    const el = this.$refs.cb;
    $(el).on('focus', () => {
      $(el).on('keyup', (e) => {
        if (e.keyCode === 32) {
          this.clicked();
        }
      });
    });
    $(el).on('blur', () => {
      $(el).off('keyup');
    });
  },

  beforeDestroy() {
    $(document).off(`escKeyup.${this.uuid}`);
  },

  methods: {
    clicked(evt) {
      if (this.stopPropagation) {
        evt.stopPropagation();
      }
      if (!this.disabled) {
        this.$emit('input', !this.value);
      } else if (this.errMsg && this.errMsg !== '') {
        calert(this.errMsg, [{
          title: this.$t('message.CANCEL'),
          action: () => {
            $(document).off(`escKeyup.${this.uuid}`);
          },
        }]);
        $(document).on(`escKeyup.${this.uuid}`, calertHide);
      }
    },
  },
};
</script>

<style lang="scss">
@import "@/assets/scss/vars.scss";

.checkbox.v-centerer {
  cursor: pointer;
  padding: .7rem .5rem;
  white-space: normal;
  border-radius: $high-border-radius;

  $border-width: 1px;

  &:hover {
    background-color: $main-hover-color;
  }

  &.block {
    display: flex;
  }

  &.padded {
    padding-left: 10px;
  }

  .tick {
    display: inline-flex;
    width: 1.15em;
    height: 1.15em;
    position: relative;
    margin-right: 0.6em;
    flex: 0 0 auto;
    box-sizing: border-box;
    border: $border-width solid $color-shizen-light-gray;
    border-radius: $medium-border-radius;

    &.round {
      border-radius: 100px;

      .tick-img {
        border-radius: 100px;
      }
    }

    .tick-img {
      position: absolute;
      top: -$border-width;
      left: -$border-width;
      right: -$border-width;
      bottom: -$border-width;
      width: calc(100% + #{$border-width * 2});
      height: calc(100% + #{$border-width * 2});
      border-radius: $medium-border-radius;
      padding: 0px;
      margin: 0px;
      transform: scale(1, 1);

      &.animated {
        transition: transform 0.2s ease-in-out;
      }
    }
  }

  &:not(.checked) .tick .tick-img {
    transform: scale(0, 0);
  }

  &.checkbox--disabled {
    opacity: 0.75;
    cursor: not-allowed;
  }

  .checkbox__title {
    flex: 1;
    min-width: 0px;
  }
}
</style>
